/** Strict date pattern: M/D/YYYY, YYYY-MM-DD, etc. Must have two separators and digits only. */
const DATE_PATTERN = /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$|^\d{4}[/-]\d{1,2}[/-]\d{1,2}$/;

/** Parse a date string into a comparable timestamp, or NaN. Only matches clear date formats. */
export function parseDate(v: string): number {
    if (!DATE_PATTERN.test(v.trim())) return NaN;
    const d = new Date(v);
    return d instanceof Date && !isNaN(d.getTime()) ? d.getTime() : NaN;
}

/** Natural compare for mixed alpha-numeric strings like "S-50", "S-123". Splits into text and number segments. */
export function naturalCompare(a: string, b: string): number {
    const re = /(\d+|\D+)/g;
    const pa = a.match(re) ?? [a];
    const pb = b.match(re) ?? [b];
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
        const sa = pa[i] ?? '';
        const sb = pb[i] ?? '';
        const na = Number(sa);
        const nb = Number(sb);
        if (!isNaN(na) && !isNaN(nb)) {
            if (na !== nb) return na - nb;
            if (sa !== sb) return sa.length - sb.length || sa.localeCompare(sb);
        } else {
            const cmp = sa.localeCompare(sb, undefined, { sensitivity: 'base' });
            if (cmp !== 0) return cmp;
        }
    }
    return 0;
}

/** Compare two display values smartly: dates by date, mixed IDs naturally, else locale string. */
export function smartCompare(a: string, b: string): number {
    if (a === '—' && b === '—') return 0;
    if (a === '—') return -1;
    if (b === '—') return 1;

    const da = parseDate(a);
    const db = parseDate(b);
    if (!isNaN(da) && !isNaN(db)) return da - db;

    return naturalCompare(a, b);
}
